/**
 * Descriptor-engine edit-session flush (#997 — Story 6.2).
 *
 * Gives the descriptor engine the save/flush/remap wiring the bespoke editors
 * already have, closing Obs 6: a pre-save attachment on a NEW unit goes
 * invisible in the editor after save because the engine never remaps the unit's
 * temp id → real id, so the post-save editor reads `base.unit.{realId}` and finds
 * nothing (the file persists fine; Supporting Documents + Summary still show it).
 *
 * This is a **Racks-delegating shim** (Story 6.2 architecture decision, Rule of
 * Three): for the Racks descriptor it converts the engine's `DescriptorInstance[]`
 * session to `RackConfiguration[]` and delegates to the proven
 * `flushRackEditSession` (Story 3.2 golden-master oracle), which creates rows via
 * REST, builds the temp→real `createdIdMap`, and calls
 * `flushRepeatableInstanceAttachmentSectionRemaps` to move each staged attachment
 * from `base.unit.{temp}` to `base.unit.{real}` server-side. Delegating reuses the
 * oracle's full online AND offline semantics (including the rack-typed offline
 * queue) byte-for-byte, so parity risk is confined to the pure converter below.
 *
 * The fully-generic descriptor flush (operating on `DescriptorInstance[]` via
 * `descriptor.offlineBinding`) is deferred to the third section migration: one
 * proven oracle is not enough concrete instances to abstract from, and each
 * bespoke `*EditSessionFlush.ts` has a section-typed offline queue whose parity
 * cannot be characterized until the section migrates live. See deferred-work.
 */

import { flushCoolingSystemEditSession } from './coolingSystemEditSessionFlush';
import { flushLargeUpsEditSession } from './largeUpsEditSessionFlush';
import { flushLiquidCoolingCduEditSession } from './liquidCoolingCduEditSessionFlush';
import { flushPowerDistributionEditSession } from './powerDistributionEditSessionFlush';
import { flushRackEditSession } from './rackEditSessionFlush';
import { flushSmallUpsEditSession } from './smallUpsEditSessionFlush';
import { parseOptionalNumber } from '../components/repeatable/repeatableSectionListUtils';
import { coolingSystemConfigurationsKeys } from '../hooks/queries/useCoolingSystemConfigurations';
import { largeUpsConfigurationsKeys } from '../hooks/queries/useLargeUpsConfigurations';
import { liquidCoolingCduConfigurationsKeys } from '../hooks/queries/useLiquidCoolingCduConfigurations';
import { powerDistributionConfigurationsKeys } from '../hooks/queries/usePowerDistributionConfigurations';
import { rackConfigurationsKeys } from '../hooks/queries/useRackConfigurations';
import { smallUpsConfigurationsKeys } from '../hooks/queries/useSmallUpsConfigurations';

import type { RepeatableInstance } from '../components/repeatableSections/types';
import type { SectionDescriptor } from '../request/sections/sectionDescriptor.types';
import type {
  CoolingSystemConfiguration,
  CoolingSystemStatus,
} from '../types/coolingSystem';
import type { LargeUpsConfiguration, LargeUpsStatus } from '../types/largeUps';
import type {
  LiquidCoolingCduConfiguration,
  LiquidCoolingCduStatus,
} from '../types/liquidCoolingCdu';
import type {
  PowerDistributionConfiguration,
  PowerDistributionStatus,
} from '../types/powerDistribution';
import type { RackConfiguration } from '../types/rack';
import type { SmallUpsConfiguration, SmallUpsStatus } from '../types/smallUps';

/** A generic descriptor-defined instance row (mirrors `DescriptorInstance`). */
type DescriptorInstanceLike = RepeatableInstance & {
  [dataField: string]: string | number | boolean | undefined;
};

/** Input shape for `RepeatableInstanceEditSessionConfig.flushSession`. */
export interface DescriptorEditSessionFlushInput {
  requestId: string;
  baseline: DescriptorInstanceLike[];
  session: DescriptorInstanceLike[];
  offlineMode: boolean;
}

/**
 * Descriptors that have a proven delegating flush shim wired into
 * {@link resolveDelegatedFlushBinding}. Reads the explicit
 * `offlineBinding.delegatedFlush` capability (#997 Story 6.4) rather than the
 * former `restResource === 'racks'` magic string, so a section opts in by
 * declaration once its shim exists (Racks + Small UPS today). The generic
 * `flushDescriptorRepeatableOperations` stays test-only until three proven
 * flushes exist (Rule of Three, Story 6.2 ADR).
 */
export function descriptorSupportsDelegatedFlush(
  descriptor: SectionDescriptor,
): boolean {
  return descriptor.offlineBinding.delegatedFlush === true;
}

/**
 * Engine `DescriptorInstance` → oracle `RackConfiguration`. Racks-specific:
 *
 * - the engine names the unit `instanceName`/`instanceStatus`, the rack contract
 *   names them `rackName`/`rackStatus` (both status unions are `'existing' | 'new'`);
 * - the engine stores numeric field values as raw editor TEXT (`descriptorEditFields`
 *   → `descriptorFieldMapToPatch` passes non-`yesNo` values through unchanged), while
 *   the rack contract (and the bespoke editor, via `parseOptionalNumber`) expects
 *   `number`. So every descriptor `number` field is coerced here — otherwise the
 *   REST payload carries `"5"` and `rackContentKey`'s `JSON.stringify` change-detector
 *   reads `{q:"5"}` ≠ a refetched `{q:5}`, flagging unchanged units dirty forever.
 *
 * The payload is confined to the descriptor's declared persistence contract
 * (`dbBinding.columns`, camelCased) — no blind `...data` leak of non-rack keys
 * (#997 Story 6.3 R3). Keyed on dbBinding rather than the registry field list
 * because real persisted columns exist outside the registry (e.g.
 * `rack_pdu_additional_info`) and must survive the round-trip.
 */
const snakeToCamel = (column: string): string =>
  column.replace(/_([a-z0-9])/g, (_match, char: string) => char.toUpperCase());

export function descriptorInstanceToRack(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): RackConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    rackStatus: instanceStatus,
    rackName: instanceName,
    displayOrder,
  } as RackConfiguration;
}

/** Inverse of {@link descriptorInstanceToRack} (drops requestId/createdAt/updatedAt). */
export function rackToDescriptorInstance(
  rack: RackConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    rackStatus,
    rackName,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = rack;
  return {
    ...data,
    id: id as number,
    instanceStatus: rackStatus,
    instanceName: rackName,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Racks engine edit session by delegating to the bespoke oracle.
 * Returns the normalized session (temp ids replaced with real ids) so the engine
 * re-seeds its baseline against the saved rows.
 */
export async function flushRacksDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineRacks = input.baseline.map(instance =>
    descriptorInstanceToRack(instance, requestIdNum, descriptor),
  );
  const sessionRacks = input.session.map(instance =>
    descriptorInstanceToRack(instance, requestIdNum, descriptor),
  );

  const flushed = await flushRackEditSession(
    input.requestId,
    baselineRacks,
    sessionRacks,
    input.offlineMode,
  );

  return flushed.map(rackToDescriptorInstance);
}

/**
 * Small UPS converters + delegating flush (#997 — Story 6.4, section #2).
 *
 * Small UPS is the SECOND section wired onto the engine's delegating flush path.
 * The mapping is simpler than Racks — the persisted `SmallUpsConfiguration`
 * already uses the generic identity columns `name` / `status` (NOT
 * `smallUpsName` / `smallUpsStatus`), so identity is a direct `name ↔
 * instanceName`, `status ↔ instanceStatus` swap. Everything else mirrors the
 * Racks converters exactly:
 *
 * - the engine stores numeric field values as raw editor TEXT, while the Small
 *   UPS contract (and the bespoke editor via `parseOptionalNumber`) expects
 *   `number`, so every descriptor `number` field is coerced here — otherwise the
 *   REST payload carries `"5"` and the flush's content-key change-detector reads
 *   `{q:"5"}` ≠ a refetched `{q:5}`, flagging unchanged units dirty forever;
 * - the payload is confined to the descriptor's declared persistence contract
 *   (`dbBinding.columns`, camelCased) — no blind `...data` leak of non-Small-UPS
 *   keys (#997 Story 6.3 R3 lesson), keyed on dbBinding rather than the registry
 *   field list so real persisted columns survive the round-trip.
 */
function normalizeSmallUpsStatus(value: unknown): SmallUpsStatus {
  return value === 'existing' ? 'existing' : 'new';
}

/** Engine `DescriptorInstance` → bespoke `SmallUpsConfiguration`. */
export function descriptorInstanceToSmallUps(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): SmallUpsConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    status: normalizeSmallUpsStatus(instanceStatus),
    name: instanceName,
    displayOrder,
  } as SmallUpsConfiguration;
}

/** Inverse of {@link descriptorInstanceToSmallUps} (drops requestId/createdAt/updatedAt). */
export function smallUpsToDescriptorInstance(
  unit: SmallUpsConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    status,
    name,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = unit;
  return {
    ...data,
    id: id as number,
    instanceStatus: status,
    instanceName: name,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Small UPS engine edit session by delegating to the proven bespoke
 * flush (`flushSmallUpsEditSession`, Story 4.5 golden-master oracle). Returns
 * the normalized session (temp ids replaced with real ids) so the engine
 * re-seeds its baseline against the saved rows and the caller can derive the
 * temp→real created-id map (Obs 6) from the id delta.
 */
export async function flushSmallUpsDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineUnits = input.baseline.map(instance =>
    descriptorInstanceToSmallUps(instance, requestIdNum, descriptor),
  );
  const sessionUnits = input.session.map(instance =>
    descriptorInstanceToSmallUps(instance, requestIdNum, descriptor),
  );

  const flushed = await flushSmallUpsEditSession(
    input.requestId,
    baselineUnits,
    sessionUnits,
    input.offlineMode,
  );

  return flushed.map(smallUpsToDescriptorInstance);
}

/**
 * Large UPS converters + delegating flush (#997 — Story 6.5, section #3).
 *
 * Large UPS is the THIRD section wired onto the engine's delegating flush path.
 * The mapping mirrors Small UPS — the persisted `LargeUpsConfiguration` uses the
 * generic identity columns `name` / `status`, so identity is a direct `name ↔
 * instanceName`, `status ↔ instanceStatus` swap. The one simplification vs Small
 * UPS: Large UPS has ZERO numeric domain fields (`powerBatteryRuntimeRequiredMin`
 * is a `string`), so `numberFieldKeys` is always empty and the coercion loop is a
 * structural no-op — retained verbatim for parity with the Racks/Small UPS
 * converters. As with the peers, the payload is confined to the descriptor's
 * declared persistence contract (`dbBinding.columns`, camelCased) — no blind
 * `...data` leak of non-Large-UPS keys (#997 Story 6.3 R3 lesson).
 */
function normalizeLargeUpsStatus(value: unknown): LargeUpsStatus {
  return value === 'existing' ? 'existing' : 'new';
}

/** Engine `DescriptorInstance` → bespoke `LargeUpsConfiguration`. */
export function descriptorInstanceToLargeUps(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): LargeUpsConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    status: normalizeLargeUpsStatus(instanceStatus),
    name: instanceName,
    displayOrder,
  } as LargeUpsConfiguration;
}

/** Inverse of {@link descriptorInstanceToLargeUps} (drops requestId/createdAt/updatedAt). */
export function largeUpsToDescriptorInstance(
  unit: LargeUpsConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    status,
    name,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = unit;
  return {
    ...data,
    id: id as number,
    instanceStatus: status,
    instanceName: name,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Large UPS engine edit session by delegating to the proven bespoke
 * flush (`flushLargeUpsEditSession`, Story 4.3 golden-master oracle). Returns
 * the normalized session (temp ids replaced with real ids) so the engine
 * re-seeds its baseline and the caller can derive the temp→real created-id map
 * (Obs 6) from the id delta.
 */
export async function flushLargeUpsDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineUnits = input.baseline.map(instance =>
    descriptorInstanceToLargeUps(instance, requestIdNum, descriptor),
  );
  const sessionUnits = input.session.map(instance =>
    descriptorInstanceToLargeUps(instance, requestIdNum, descriptor),
  );

  const flushed = await flushLargeUpsEditSession(
    input.requestId,
    baselineUnits,
    sessionUnits,
    input.offlineMode,
  );

  return flushed.map(largeUpsToDescriptorInstance);
}

/**
 * Cooling System converters + delegating flush (#997 — Story 6.6, section #4).
 *
 * Cooling is the FOURTH section wired onto the engine's delegating flush path.
 * The mapping mirrors Large/Small UPS — the persisted `CoolingSystemConfiguration`
 * uses the generic identity columns `name` / `status`, so identity is a direct
 * `name ↔ instanceName`, `status ↔ instanceStatus` swap. Cooling has ONE
 * `number`-typed control (`coolingTempRisePrefThroughItEquipment`), but that
 * column is `NVARCHAR(255)` (a STRING on the entity), so the descriptor retypes
 * it `text` (see `coolingSystem.descriptor.ts`) and `numberFieldKeys` is empty:
 * coercing the stored string `"5"` to a `number` would false-dirty every
 * untouched unit forever (round-tripped `{f:5}` never equals raw baseline
 * `{f:"5"}`). The coercion loop is retained verbatim for parity with the
 * Racks/UPS converters. As with the peers, the payload is confined to the
 * descriptor's declared persistence contract (`dbBinding.columns`, camelCased) —
 * no blind `...data` leak of non-Cooling keys (#997 Story 6.3 R3 lesson).
 */
function normalizeCoolingSystemStatus(value: unknown): CoolingSystemStatus {
  return value === 'existing' ? 'existing' : 'new';
}

/** Engine `DescriptorInstance` → bespoke `CoolingSystemConfiguration`. */
export function descriptorInstanceToCoolingSystem(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): CoolingSystemConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    status: normalizeCoolingSystemStatus(instanceStatus),
    name: instanceName,
    displayOrder,
  } as CoolingSystemConfiguration;
}

/** Inverse of {@link descriptorInstanceToCoolingSystem} (drops requestId/createdAt/updatedAt). */
export function coolingSystemToDescriptorInstance(
  unit: CoolingSystemConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    status,
    name,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = unit;
  return {
    ...data,
    id: id as number,
    instanceStatus: status,
    instanceName: name,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Cooling System engine edit session by delegating to the proven bespoke
 * flush (`flushCoolingSystemEditSession`, Story 4.6 golden-master oracle).
 * Returns the normalized session (temp ids replaced with real ids) so the engine
 * re-seeds its baseline and the caller can derive the temp→real created-id map
 * (Obs 6) from the id delta.
 */
export async function flushCoolingSystemDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineUnits = input.baseline.map(instance =>
    descriptorInstanceToCoolingSystem(instance, requestIdNum, descriptor),
  );
  const sessionUnits = input.session.map(instance =>
    descriptorInstanceToCoolingSystem(instance, requestIdNum, descriptor),
  );

  const flushed = await flushCoolingSystemEditSession(
    input.requestId,
    baselineUnits,
    sessionUnits,
    input.offlineMode,
  );

  return flushed.map(coolingSystemToDescriptorInstance);
}

/**
 * Power Distribution converters + delegating flush (#997 — Story 6.7, section #5).
 *
 * Power Distribution is the FIFTH and FINAL section wired onto the engine's
 * delegating flush path. The mapping mirrors Cooling / Large / Small UPS — the
 * persisted `PowerDistributionConfiguration` uses the generic identity columns
 * `name` / `status`, so identity is a direct `name ↔ instanceName`,
 * `status ↔ instanceStatus` swap. Power Distribution has ZERO `number`-typed
 * descriptor fields: `powerBuswayLengthFt` renders as a numeric `FormInput` but is
 * typed `text` on the descriptor (the live control coerces via `parseOptionalNumber`
 * client-side, and the descriptor was already correct at Story 4.4), so
 * `numberFieldKeys` is empty and the coercion loop never fires. The loop is
 * retained verbatim for parity with the Racks/UPS/Cooling converters. As with the
 * peers, the payload is confined to the descriptor's declared persistence contract
 * (`dbBinding.columns`, camelCased) — no blind `...data` leak of non-PD keys
 * (#997 Story 6.3 R3 lesson).
 */
function normalizePowerDistributionStatus(
  value: unknown,
): PowerDistributionStatus {
  return value === 'existing' ? 'existing' : 'new';
}

/** Engine `DescriptorInstance` → bespoke `PowerDistributionConfiguration`. */
export function descriptorInstanceToPowerDistribution(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): PowerDistributionConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    status: normalizePowerDistributionStatus(instanceStatus),
    name: instanceName,
    displayOrder,
  } as PowerDistributionConfiguration;
}

/** Inverse of {@link descriptorInstanceToPowerDistribution} (drops requestId/createdAt/updatedAt). */
export function powerDistributionToDescriptorInstance(
  unit: PowerDistributionConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    status,
    name,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = unit;
  return {
    ...data,
    id: id as number,
    instanceStatus: status,
    instanceName: name,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Power Distribution engine edit session by delegating to the proven
 * bespoke flush (`flushPowerDistributionEditSession`, Story 4.4 golden-master
 * oracle). Returns the normalized session (temp ids replaced with real ids) so the
 * engine re-seeds its baseline and the caller can derive the temp→real created-id
 * map (Obs 6) from the id delta.
 */
export async function flushPowerDistributionDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineUnits = input.baseline.map(instance =>
    descriptorInstanceToPowerDistribution(instance, requestIdNum, descriptor),
  );
  const sessionUnits = input.session.map(instance =>
    descriptorInstanceToPowerDistribution(instance, requestIdNum, descriptor),
  );

  const flushed = await flushPowerDistributionEditSession(
    input.requestId,
    baselineUnits,
    sessionUnits,
    input.offlineMode,
  );

  return flushed.map(powerDistributionToDescriptorInstance);
}

/**
 * Liquid Cooling CDU converters + delegating flush (#997 — Story 6.8, section #6).
 *
 * Liquid Cooling is the SIXTH and FINAL section wired onto the engine's
 * delegating flush path. The persisted `LiquidCoolingCduConfiguration` uses the
 * generic identity columns `name` / `status` (`LiquidCoolingCduStatus` =
 * `'existing' | 'new'`), so identity is a direct `name ↔ instanceName`,
 * `status ↔ instanceStatus` swap. Liquid Cooling has ZERO `number`-typed
 * descriptor fields (every per-CDU field is `text` / `multilineText` / `dropdown`
 * / `yesNo`; the shared `lcLiquidToChipDesired` is `yesNo`), so `numberFieldKeys`
 * is empty and the coercion loop never fires. The loop is retained verbatim for
 * parity with the Racks/UPS/Cooling/PD converters. As with the peers, the payload
 * is confined to the descriptor's declared persistence contract
 * (`dbBinding.columns`, camelCased) — no blind `...data` leak of non-LC keys
 * (#997 Story 6.3 R3 lesson).
 */
function normalizeLiquidCoolingCduStatus(
  value: unknown,
): LiquidCoolingCduStatus {
  return value === 'existing' ? 'existing' : 'new';
}

/** Engine `DescriptorInstance` → bespoke `LiquidCoolingCduConfiguration`. */
export function descriptorInstanceToLiquidCoolingCdu(
  instance: DescriptorInstanceLike,
  requestId: number,
  descriptor: SectionDescriptor,
): LiquidCoolingCduConfiguration {
  const { id, instanceStatus, instanceName, displayOrder, ...data } = instance;

  const persistedKeys = new Set(descriptor.dbBinding.columns.map(snakeToCamel));
  const numberFieldKeys = new Set(
    descriptor.fields
      .filter(field => field.type === 'number')
      .map(field => field.dataField),
  );
  const coerced: Record<string, string | number | boolean | undefined> = {};
  for (const [key, value] of Object.entries(data)) {
    if (!persistedKeys.has(key)) {
      continue;
    }
    coerced[key] =
      numberFieldKeys.has(key) && typeof value === 'string'
        ? parseOptionalNumber(value)
        : value;
  }

  return {
    ...coerced,
    id,
    requestId,
    status: normalizeLiquidCoolingCduStatus(instanceStatus),
    name: instanceName,
    displayOrder,
  } as LiquidCoolingCduConfiguration;
}

/** Inverse of {@link descriptorInstanceToLiquidCoolingCdu} (drops requestId/createdAt/updatedAt). */
export function liquidCoolingCduToDescriptorInstance(
  unit: LiquidCoolingCduConfiguration,
): DescriptorInstanceLike {
  const {
    id,
    requestId: _requestId,
    status,
    name,
    createdAt: _createdAt,
    updatedAt: _updatedAt,
    displayOrder,
    ...data
  } = unit;
  return {
    ...data,
    id: id as number,
    instanceStatus: status,
    instanceName: name,
    displayOrder,
  } as DescriptorInstanceLike;
}

/**
 * Flush a Liquid Cooling CDU engine edit session by delegating to the proven
 * bespoke flush (`flushLiquidCoolingCduEditSession`, Story 4.2 golden-master
 * oracle). Returns the normalized session (temp ids replaced with real ids) so the
 * engine re-seeds its baseline and the caller can derive the temp→real created-id
 * map (Obs 6) from the id delta.
 */
export async function flushLiquidCoolingCduDescriptorEditSession(
  input: DescriptorEditSessionFlushInput,
  descriptor: SectionDescriptor,
): Promise<DescriptorInstanceLike[]> {
  const requestIdNum = Number.parseInt(input.requestId, 10) || 0;
  const baselineUnits = input.baseline.map(instance =>
    descriptorInstanceToLiquidCoolingCdu(instance, requestIdNum, descriptor),
  );
  const sessionUnits = input.session.map(instance =>
    descriptorInstanceToLiquidCoolingCdu(instance, requestIdNum, descriptor),
  );

  const flushed = await flushLiquidCoolingCduEditSession(
    input.requestId,
    baselineUnits,
    sessionUnits,
    input.offlineMode,
  );

  return flushed.map(liquidCoolingCduToDescriptorInstance);
}

/**
 * Per-descriptor delegating-flush binding (#997 — Story 6.4, AC 4). Resolves the
 * section-specific shape converters + flush shim for a descriptor that opts into
 * the engine save/flush path (`offlineBinding.delegatedFlush`). Replaces the
 * adapter's hardcoded rack symbols so delegated-flush routing is parameterized
 * per descriptor; peers without a shim resolve `undefined` and keep the pre-6.2
 * behavior. The generic (descriptor-driven) flush stays test-only (Rule of
 * Three) — this registry adds concrete bindings section-by-section instead.
 */
export interface DescriptorDelegatedFlushBinding {
  /** Persisted row → engine instance (display in-converter). */
  toEngineInstance: (
    persisted: DescriptorInstanceLike,
  ) => DescriptorInstanceLike;
  /** Engine instance → persisted row (out-converter for the editedData patch). */
  toPersistedInstance: (
    instance: DescriptorInstanceLike,
    requestId: number,
    descriptor: SectionDescriptor,
  ) => DescriptorInstanceLike;
  /** Delegate the flush to the section's proven bespoke oracle. */
  flush: (
    input: DescriptorEditSessionFlushInput,
    descriptor: SectionDescriptor,
  ) => Promise<DescriptorInstanceLike[]>;
  /**
   * Post-flush ROW-query invalidation keys for this section (list + grouped) so
   * the flush refetches only the SECTION's queries — no rack keys fire for Small
   * UPS and vice-versa (#997 Story 6.4 AC 4). Colocated with the converters/flush
   * here (#997 delegated-flush registry collapse, Rule-of-Three prep) so a section
   * is wired in ONE place; the shared `offlineRequestsKeys.detail` embed
   * invalidation is added by the adapter for every delegated flush.
   */
  listQueryKeys: (requestId: string) => Array<readonly unknown[]>;
}

export function resolveDelegatedFlushBinding(
  descriptor: SectionDescriptor,
): DescriptorDelegatedFlushBinding | undefined {
  if (!descriptor.offlineBinding.delegatedFlush) {
    return undefined;
  }
  switch (descriptor.id) {
    case 'racks':
      return {
        toEngineInstance: item =>
          rackToDescriptorInstance(item as unknown as RackConfiguration),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToRack(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushRacksDescriptorEditSession,
        listQueryKeys: requestId => [
          rackConfigurationsKeys.list(requestId),
          rackConfigurationsKeys.grouped(requestId),
        ],
      };
    case 'small-ups':
      return {
        toEngineInstance: item =>
          smallUpsToDescriptorInstance(
            item as unknown as SmallUpsConfiguration,
          ),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToSmallUps(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushSmallUpsDescriptorEditSession,
        listQueryKeys: requestId => [
          smallUpsConfigurationsKeys.list(requestId),
          smallUpsConfigurationsKeys.grouped(requestId),
        ],
      };
    case 'large-ups':
      return {
        toEngineInstance: item =>
          largeUpsToDescriptorInstance(
            item as unknown as LargeUpsConfiguration,
          ),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToLargeUps(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushLargeUpsDescriptorEditSession,
        listQueryKeys: requestId => [
          largeUpsConfigurationsKeys.list(requestId),
          largeUpsConfigurationsKeys.grouped(requestId),
        ],
      };
    case 'cooling-system':
      return {
        toEngineInstance: item =>
          coolingSystemToDescriptorInstance(
            item as unknown as CoolingSystemConfiguration,
          ),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToCoolingSystem(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushCoolingSystemDescriptorEditSession,
        listQueryKeys: requestId => [
          coolingSystemConfigurationsKeys.list(requestId),
          coolingSystemConfigurationsKeys.grouped(requestId),
        ],
      };
    case 'power-distribution':
      return {
        toEngineInstance: item =>
          powerDistributionToDescriptorInstance(
            item as unknown as PowerDistributionConfiguration,
          ),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToPowerDistribution(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushPowerDistributionDescriptorEditSession,
        listQueryKeys: requestId => [
          powerDistributionConfigurationsKeys.list(requestId),
          powerDistributionConfigurationsKeys.grouped(requestId),
        ],
      };
    case 'liquid-cooling':
      return {
        toEngineInstance: item =>
          liquidCoolingCduToDescriptorInstance(
            item as unknown as LiquidCoolingCduConfiguration,
          ),
        toPersistedInstance: (instance, requestId, activeDescriptor) =>
          descriptorInstanceToLiquidCoolingCdu(
            instance,
            requestId,
            activeDescriptor,
          ) as unknown as DescriptorInstanceLike,
        flush: flushLiquidCoolingCduDescriptorEditSession,
        listQueryKeys: requestId => [
          liquidCoolingCduConfigurationsKeys.list(requestId),
          liquidCoolingCduConfigurationsKeys.grouped(requestId),
        ],
      };
    default:
      return undefined;
  }
}
